import { useEffect, useState } from 'react'
import { api } from '../services/api'

export interface Branding {
  readonly logoUrl: string | null
  readonly themeColor: string | null
  readonly orgName: string | null
}

const DEFAULT_BRANDING: Branding = { logoUrl: null, themeColor: null, orgName: null }

// Module-scoped cache keyed on (orgId ?? ''): each org's branding is
// fetched at most once per page load. LoginPage calls without an orgId
// (single-tenant edge fallback); the authenticated Layout calls with
// the user's orgId.
const cache = new Map<string, Branding>()
const inflight = new Map<string, Promise<Branding>>()

async function load(orgId: string | undefined): Promise<Branding> {
  const key = orgId ?? ''
  const existing = cache.get(key)
  if (existing) return existing
  const pending = inflight.get(key)
  if (pending) return pending
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''
  const fetchP = api
    .get<Branding>(`/meta/branding${query}`)
    .then((res) => {
      const b = res.data ?? DEFAULT_BRANDING
      cache.set(key, b)
      return b
    })
    .catch(() => DEFAULT_BRANDING)
    .finally(() => {
      inflight.delete(key)
    })
  inflight.set(key, fetchP)
  return fetchP
}

export function useBranding(orgId?: string): Branding {
  const [branding, setBranding] = useState<Branding>(() => cache.get(orgId ?? '') ?? DEFAULT_BRANDING)
  useEffect(() => {
    let cancelled = false
    load(orgId).then((b) => {
      if (!cancelled) setBranding(b)
    })
    return () => {
      cancelled = true
    }
  }, [orgId])
  return branding
}

// Exported for tests to reset the cache between cases.
export function _resetBrandingCache(): void {
  cache.clear()
  inflight.clear()
}
